import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Icon } from '@iconify/react';

/**
 * OnboardingLayout - 統一的 onboarding 頁面佈局
 * 參考圖片風格：左側插圖區 + 右側內容區
 */
function OnboardingLayout({
  currentStep,
  totalSteps = 6,
  title,
  illustration,
  children,
  onBack,
  showBackButton = true,
  progressColor = '#60A5FA' // 淺藍色
}) {
  const navigate = useNavigate();
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Illustration Area */}
      <div className="hidden lg:flex lg:w-2/5 bg-gradient-to-br from-blue-100 via-blue-50 to-white p-12 flex-col justify-between relative overflow-hidden">
        {/* Logo */}
        <Link to="/posts-and-comments" className="relative z-10 flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity">
          <img
            src="/images/viralarc-logo.png"
            alt="ViralArc.ai"
            className="w-8 h-8"
            onError={(e) => {
              // Fallback to text logo if image not found
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          <div
            className="w-8 h-8 rounded-lg bg-blue-600 items-center justify-center text-white font-bold text-lg"
            style={{ display: 'none' }}
          >
            V
          </div>
          <span className="font-bold text-lg text-gray-900">
            ViralArc.ai
          </span>
        </Link>

        {/* Illustration - Simple and Clean */}
        <div className="relative z-10 flex items-center justify-center flex-1">
          {illustration}
        </div>

        {/* Bottom Info */}
        <div className="relative z-10 flex items-center justify-between text-sm text-gray-600">
          <span>© 2025 ViralArc</span>
          <div className="flex gap-4">
            <Link to="/terms-of-service" className="hover:text-gray-900 transition-colors">
              Terms of Service
            </Link>
            <Link to="/privacy-policy" className="hover:text-gray-900 transition-colors">
              Privacy Policy
            </Link>
          </div>
        </div>
      </div>

      {/* Right Side - Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header with Help */}
        <div className="flex items-center justify-end p-6 border-b border-gray-100">
          <div className="text-sm text-gray-500">
            Need help?{' '}
            <a href="mailto:official@viralarc-ai.com" className="text-blue-500 hover:text-blue-600 font-medium">
              Contact support
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-2xl">
            {/* Title */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                {title}
              </h1>
            </div>

            {/* Content */}
            <div className="mb-12">
              {children}
            </div>

            {/* Progress Bar */}
            <div className="mt-12">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">
                  {currentStep} OF {totalSteps}
                </span>
                {showBackButton && onBack && (
                  <button
                    onClick={onBack}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  >
                    <Icon icon="mdi:arrow-left" width={16} />
                    Back
                  </button>
                )}
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500 ease-out"
                  style={{
                    width: `${progress}%`,
                    background: progressColor
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingLayout;
